import { useState } from "react";

// Drop-in replacement for <img> — defers offscreen images to the browser's
// native lazy-loader and shows a shimmering placeholder in the image's own
// footprint (instead of a blank gap) until it actually decodes. Renders a
// single <img> so it never disturbs layouts that depend on the element
// itself (carousels, avatar badges, etc).
export default function LazyImage({ src, alt, style, onClick, eager, onLoad, ...rest }) {
  // Tracks WHICH src last finished loading, rather than a plain loaded
  // boolean reset from a separate useEffect — that ran a render after commit,
  // so a cached image's onLoad could fire first and flip loaded to true, and
  // the effect would then stomp it back to false, leaving the shimmer stuck
  // even though the image was already showing. Deriving `loaded` this way
  // instead makes it correct the instant `src` changes, same render, no race.
  //
  // Callers key list rows by the content (a comment/notification id), not by
  // the image URL — so when that same row's actor changes their avatar and
  // the list re-fetches, React reuses this exact instance with just a new
  // `src`, and this is what makes the shimmer correctly reappear for it.
  const [loadedSrc, setLoadedSrc] = useState(null);
  const loaded = loadedSrc === src;

  return (
    <img
      src={src}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      onClick={onClick}
      onLoad={e => {
        setLoadedSrc(src);
        onLoad?.(e);
      }}
      // A broken/missing image (deleted Storage file, bad URL) never fires
      // onLoad — without this, the shimmer would animate forever instead of
      // settling into the browser's normal broken-image state.
      onError={() => setLoadedSrc(src)}
      style={
        loaded
          ? style
          : {
              ...style,
              background:
                "linear-gradient(110deg, var(--shimmer-base) 30%, var(--shimmer-highlight) 45%, var(--shimmer-base) 60%)",
              backgroundSize: "200% 100%",
              animation: "virsa-shimmer 1.4s ease-in-out infinite",
            }
      }
      {...rest}
    />
  );
}
